import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, Subject, from, merge, throwError, timer } from 'rxjs';
import { filter, finalize, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type { AppEnvironment } from '../../environments/environment.model';
import { ConnectionHealthService, ConnectionVerdict } from './connection-health.service';

// The environment.*.ts files are plain object literals, so their inferred type does not carry the
// optional members declared on AppEnvironment.
const env = environment as AppEnvironment;

/**
 * Endpoints that are legitimately slow. These get a long budget so the timeout never fires on a
 * healthy-but-slow query — the point is to catch hung connections, not to police latency.
 */
const SLOW_ENDPOINT_PATTERNS: readonly RegExp[] = [
  /\/order\/get-lp-image\b/,
  /\/vendor-membership-lookup\b/,
  /\/export\b/,
  /\/upload\b/,
  // CWS-1637: CSV downloads stream for longer than the default budget; with progress
  // events (ApiService.getBlob) the budget bounds silence between chunks, not the download.
  /\/download\/[\w-]*csv\b/,
];

/** Long-lived streams must never be timed out. */
const STREAMING_ENDPOINT_PATTERNS: readonly RegExp[] = [/\/events\/stream\b/];

const DEFAULT_BUDGET_MS = 20_000;
const SLOW_BUDGET_MS = 60_000;

/**
 * How long a request may go unanswered before we ask the network whether it is still there.
 * This is not an abort threshold — crossing it only triggers a probe.
 */
const SOFT_PROBE_DELAY_MS = 2000;

function softProbeDelayMs(): number {
  return env.connectionResilience?.softProbeDelayMs ?? SOFT_PROBE_DELAY_MS;
}

/**
 * Fails a request that has stopped making progress instead of letting it hang.
 *
 * `ApiService` issues plain `HttpClient` calls with no timeout of any kind, so a request written to
 * a silently-dead pooled connection sits in "Receive" until the OS abandons it — 35-40s in the
 * incident this was written for, during which the UI shows nothing and the user retries by hand.
 *
 * Two tiers, because elapsed time alone cannot tell a dead path from a slow query:
 *
 *  - After a short soft delay with no response, a cheap connectivity probe runs. A reachable
 *    network leaves the request completely alone; an unreachable one fails it immediately. Dead
 *    paths are therefore detected in seconds rather than at the budget.
 *  - The per-endpoint budget remains as a backstop for the case where the network is fine but
 *    nothing ever comes back. Budgets are deliberately generous — they bound an indefinite hang,
 *    they are not a latency policy.
 *
 * No automatic retry here on purpose — mutating endpoints have no idempotency keys yet, so a retry
 * could double-apply. Retry lands after those do.
 */
@Injectable()
export class HttpTimeoutInterceptor implements HttpInterceptor {
  constructor(private readonly connectionHealth: ConnectionHealthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const budgetMs = budgetFor(req.url);
    if (budgetMs === null) {
      return next.handle(req);
    }

    // Fires once the request has settled (response, error or cancel) so the watchdog below is
    // torn down with it. Without this, `merge` kept the stream open until the soft-probe timer
    // had fired and the probe had returned — every `finalize` loading flag in the app cleared
    // ~2s after the response was already on screen, and each request cost an extra probe.
    const settled$ = new Subject<void>();
    const response$ = next.handle(req).pipe(
      tap(event => {
        if (event.type === HttpEventType.Response) {
          this.connectionHealth.markSuccess();
        }
      }),
      finalize(() => {
        settled$.next();
        settled$.complete();
      })
    );

    // Merged rather than raced: this never emits a value, so the response stream is untouched
    // while the path is healthy. If it errors, merge tears down the HTTP subscription with it,
    // which cancels the request. A soft timer cannot simply abort-and-resume, because once
    // the request is torn down it cannot be revived.
    return merge(response$, this.deadNetworkSignal(req, budgetMs).pipe(takeUntil(settled$))).pipe(
      // Backstop only. The soft probe above is what detects a dead path quickly; this bounds the
      // case where the network is fine but nothing ever comes back. `each` measures the gap
      // between events, so progress events keep it alive.
      timeout({
        each: budgetMs,
        with: () => this.classifyAndFail(req, budgetMs),
      })
    );
  }

  /**
   * Fails the request as soon as the network is known to be dead, rather than waiting out the full
   * budget. Stays silent — completing without emitting — whenever the path is reachable, so a
   * legitimately slow endpoint runs to its budget untouched.
   */
  private deadNetworkSignal(req: HttpRequest<any>, budgetMs: number): Observable<never> {
    return timer(softProbeDelayMs()).pipe(
      switchMap(() => from(this.connectionHealth.probe())),
      filter(verdict => verdict === 'network-unreachable'),
      take(1),
      switchMap(() => {
        this.connectionHealth.recordStalledRequest({
          method: req.method,
          url: req.url,
          budgetMs,
          verdict: 'network-unreachable',
        });
        return throwError(
          () =>
            new HttpErrorResponse({
              url: req.url,
              status: 0,
              statusText: 'Network unreachable',
              error: new Error(
                'No response yet and the connectivity probe failed; the network path is down.'
              ),
            })
        );
      })
    );
  }

  private classifyAndFail(req: HttpRequest<any>, budgetMs: number): Observable<never> {
    return from(this.connectionHealth.probe()).pipe(
      switchMap((verdict: ConnectionVerdict) => {
        this.connectionHealth.recordStalledRequest({
          method: req.method,
          url: req.url,
          budgetMs,
          verdict,
        });

        const reachable = verdict === 'network-reachable';
        return throwError(
          () =>
            new HttpErrorResponse({
              url: req.url,
              status: 0,
              statusText: reachable
                ? 'Request timed out (network reachable)'
                : 'Network unreachable',
              error: new Error(
                reachable
                  ? `No response within ${budgetMs}ms; the network is reachable, so the server or this endpoint is slow.`
                  : `No response within ${budgetMs}ms and the connectivity probe also failed; the network path is down.`
              ),
            })
        );
      })
    );
  }
}

/** Budget for a URL, or null when the request must not be timed out at all. */
export function budgetFor(url: string): number | null {
  if (STREAMING_ENDPOINT_PATTERNS.some(pattern => pattern.test(url))) {
    return null;
  }
  if (env.connectionResilience?.httpTimeoutEnabled === false) {
    return null;
  }
  if (SLOW_ENDPOINT_PATTERNS.some(pattern => pattern.test(url))) {
    return env.connectionResilience?.slowRequestTimeoutMs ?? SLOW_BUDGET_MS;
  }
  return env.connectionResilience?.defaultRequestTimeoutMs ?? DEFAULT_BUDGET_MS;
}
